import React, {useEffect, useRef, useState} from "react";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, TextField} from "@material-ui/core";
import {t} from "i18next";
import Box from "@mui/material/Box";
import {Building, Room} from "../../API";
import ManageBuildingAssignedRoomsComponent from "./ManageBuildingAssignedRoomsComponent";
import ManageBuildingAvailableRoomsComponent from "./ManageBuildingAvailableRoomsComponent";
import {IOrgUnit} from "../../hooks/useOrgunit";
import {updateSecureBuilding as updateBuildingTemplate, updateSecureRoom} from "../../graphql/mutations";
import {useRoomList} from "../../hooks/useRoomList";
import {gql, useMutation, useQuery} from "@apollo/client";
import {listBuildings} from "../../graphql/queries";
import {checkForValidInputNaming, comparatorAlphanumericValues} from "../../Utils/Helpers";
import {useErrorContext} from "../../hooks/useErrorContext";
import Alert from "@material-ui/lab/Alert";
import {RoleSelectionComponent} from "./RoleSelectionComponent";
import {maxBuildingNameLength} from "../../Utils/commons";
import {Stack} from "@mui/material";
import CloseButton from "../Buttons/CloseButton";
import {useDeviceMediaType} from "../../hooks/useDeviceMediaType";

const UPDATE_TYPE_FLOOR_AND_BUILDING = "updateFloorAndBuilding";

interface Props {
    showBuildingEditComponent: boolean;
    setShowBuildingEditComponent: (value: boolean) => void;
    selectedBuilding: Building;
    orgUnitList: IOrgUnit[];
}
enum INPUT_ERROR  {
    BUILDING,
    FLOOR
}

const ManageBuildingComponent: React.FC<Props> = (props) => {
    const {showBuildingEditComponent, setShowBuildingEditComponent, selectedBuilding} = props;
    const nameInputRef = useRef<HTMLInputElement>(null);
    const [newBuildingName, setNewBuildingName] = useState<string>("");
    const [seatConfEmail, setSeatConfEmail] = useState<string>(selectedBuilding?.seatConfEmail || "");
    const [showNoValidSeatConfigEmail, setShowNoValidSeatConfigEmail] = useState<boolean>(false);
    const [assignedRooms, setAssignedRooms] = useState<Room[]>([]);
    const [availableRooms, setAvailableRooms] = useState<Room[]>([]);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [roleIds, setRoleIds] = useState<string[]>(selectedBuilding.roleIds);
    const allRooms: Room[] = [...assignedRooms, ...availableRooms]
    const [updatedRoomsIds,setUpdatedRoomsIds] = useState<string[]>([])
    const {rooms: allRoomsResult, roomsLoading} = useRoomList();
    const [errors,setErrors]=useState<INPUT_ERROR|undefined>(undefined)

    const [updateBuildingRequest] = useMutation(gql(updateBuildingTemplate));
    const [updateRoomRequest] = useMutation((gql(updateSecureRoom)));

    const {reportError} = useErrorContext();
    const {refetch} = useQuery(gql(listBuildings));

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const input = event.target.value;
        setNewBuildingName(input);
        setShowAlert(false);
    };

    const addUpdatedRoomsIds = (id: string) => {
        setUpdatedRoomsIds((prev) =>
            prev.includes(id) ? prev : [...prev, id]
        );
    }

    const handleConfEmailChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const input = event.target.value;
        setSeatConfEmail(input);
        setShowAlert(false);
    };

    const showErrorMsg = (errorType:INPUT_ERROR) => {
        setErrors(errorType)
        setShowAlert(true);
    };

    const isConflictingBuildingName = async (): Promise<boolean> => {
        const queryBuildings = await refetch().catch((err) => reportError(err, "A connection error occurred", "ManageBuildingComponent"));
        if (queryBuildings === undefined) return true;
        const newBuildingNameLowerCase = newBuildingName.toLowerCase().trim();
        return queryBuildings!.data.listBuildings.items.some((building: Building) => {
            return comparatorAlphanumericValues(building.buildingName.toLowerCase().trim(), newBuildingNameLowerCase) === 0;
        });
    };

    const shouldBuildingBeUpdated = () =>
        newBuildingName.trim() !== selectedBuilding.buildingName || seatConfEmail.trim() !== selectedBuilding.seatConfEmail;
    const isFloorConflictingForAssignedRooms = ()=>{
        const seenFloors: number[] = [];

        for (const { floor } of assignedRooms) {
            const validFloorType = floor ?? 0;
            if (seenFloors.includes(validFloorType)) return true;
            seenFloors.push(validFloorType);
        }

        return false;
    }

    const assignAndResetState = async () => {
        if(isFloorConflictingForAssignedRooms()){
            showErrorMsg(INPUT_ERROR.FLOOR)
            return;
        }
       else if (newBuildingName.trim() !== selectedBuilding.buildingName.trim() && await isConflictingBuildingName()) {
            showErrorMsg(INPUT_ERROR.BUILDING)
            return;
        }
       else{
            if (shouldBuildingBeUpdated()){
                updateBuilding().catch(() => {
                    showErrorMsg(INPUT_ERROR.BUILDING)
                    return;
                })
            }
            updateRooms()
            if(!showAlert){
                setShowBuildingEditComponent(false);
            }
        }
    };

    const updateBuilding = async () => {
        updateBuildingRequest({
            variables: {
                input: {
                    buildingId: selectedBuilding.buildingId,
                    buildingName: newBuildingName.trim(),
                    roleIds: roleIds,
                    seatConfEmail: seatConfEmail
                },
            },
        }).catch(() => {
            showErrorMsg(INPUT_ERROR.BUILDING);
        });
    };

    const  updateRooms = () => {
        allRooms.forEach(room =>  {
            if(updatedRoomsIds.includes(room.roomId)){

                // TODO maybe only include floor or buildingId when it was changed
                // @ts-ignore
                delete room!.createdAt
                // @ts-ignore
                delete room!.updatedAt
                // @ts-ignore
                delete room!.seats

                const updateFloorAndBuilding = {
                    roomId: room.roomId,
                    buildingId: room.buildingId,
                    floor:room.floor
                };
                 const updateFloorOnly = {
                    roomId: room.roomId,
                     floor:room.floor
                };
                 const roomStateInDB:Room = allRoomsResult.filter(r => r.roomId === room.roomId)[0]
                 const isBuildingChanged = (roomStateInDB.buildingId !== room.buildingId)? true:false
                 const updateInput =  isBuildingChanged ? {...updateFloorAndBuilding}:{...updateFloorOnly}

                updateRoomRequest({
                    variables: {
                        input: {
                            ...updateInput
                        },
                        updateType: UPDATE_TYPE_FLOOR_AND_BUILDING
                    }
                }).catch(error => {
                    // TODO why connection error
                    reportError(error, "A connection error occurred", "ManageBuildingComponent");
                })
            }})
        }


    const closeWindow = () => {
        setShowBuildingEditComponent(false);
    };

    useEffect(() => {
        if (!roomsLoading) {
            setAssignedRooms(allRoomsResult.filter(room => room.buildingId === selectedBuilding.buildingId))
            setAvailableRooms(allRoomsResult.filter(room => room.buildingId === null));
            setNewBuildingName(selectedBuilding.buildingName)
        }
    }, [allRoomsResult]);

    useEffect(()=>{
        if(!showAlert) return  ;
        const timeout = setTimeout(() => {
            setShowAlert(false);
            setErrors(undefined)
        }, 4000);
        return ()=>{
            clearTimeout(timeout)
        }
    },[showAlert,errors])
    function emailValidityCheck(mail: string, error: any) {
        const mailValidationRegex = "^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,}$";
        if (mail) {
            if (mail.match(mailValidationRegex)) {
                error(false);
            } else {
                error(true);
            }
        } else {
            error(false);
        }
    }

    const {isNoFullscreen, isMobile} = useDeviceMediaType()


    const renderErrorMessage = ()=>{
        if(errors === INPUT_ERROR.BUILDING) return t("building-error-create-alert-title")
        if(errors === INPUT_ERROR.FLOOR) return  t("building-error-floor-not-valid")
    }
    return (
        <Dialog fullWidth={true} maxWidth={"lg"} style={{width: "100%", height: "100%", flexGrow: 1}}
                open={showBuildingEditComponent} data-testid={"buildingManagerInEdit"}>

            {showAlert && <Alert severity="error"
                                 data-testid={"building-name-error"}>{renderErrorMessage()}</Alert>}
            <DialogTitle>{t("building-manage-title")} </DialogTitle>
            <DialogContent>
                <Stack
                    direction="column"
                    spacing={2}
                    sx={{
                        justifyContent: "flex-start",
                        alignItems: "stretch",
                    }}
                >
                    <Box style={{paddingTop: "1rem", width: "100%"}}>
                        <TextField
                            style={{marginRight: "10px", width: "100%"}}
                            label={t("building-name-of-building")}
                            value={newBuildingName === undefined || newBuildingName.length === 0 ? selectedBuilding.buildingName : newBuildingName}
                            onChange={handleInputChange}
                            inputRef={nameInputRef}
                            inputProps={{"data-testid": "Building-Name-text"}}
                        />
                    </Box>

                    <Divider style={{marginBottom: "0rem"}}/>

                    <Box style={{paddingTop: "1rem", width: "100%"}}>
                        <TextField
                            style={{marginRight: "10px", width: "100%"}}
                            label={t("seat-config-email")}
                            value={seatConfEmail}
                            error={showNoValidSeatConfigEmail}
                            helperText={showNoValidSeatConfigEmail && t("building-not-valid-email")}
                            onChange={handleConfEmailChange}
                            onBlur={() => emailValidityCheck(seatConfEmail, setShowNoValidSeatConfigEmail)}
                            inputProps={{type: "email"}}
                        />
                    </Box>
                    <Divider style={{marginBottom: "0rem"}}/>
                    <Box style={{paddingTop: "1rem", width: "100%"}}>
                        <ManageBuildingAssignedRoomsComponent
                            orgUnitList={props.orgUnitList}
                            assignedRooms={assignedRooms}
                            setAssignedRooms={setAssignedRooms}
                            availableRooms={availableRooms}
                            setAvailableRooms={setAvailableRooms}
                            addUpdatedRoomsIds={addUpdatedRoomsIds}
                        />
                    </Box>
                    <Divider style={{marginBottom: "0rem"}}/>
                    <Box style={{paddingTop: "1rem", width: "100%", marginBottom: "-2.0rem"}}>
                    </Box>
                    <Box style={{paddingTop: "1rem", width: "100%"}}>
                        <ManageBuildingAvailableRoomsComponent
                            assignedRooms={assignedRooms}
                            setAssignedRooms={setAssignedRooms}
                            availableRooms={availableRooms}
                            setAvailableRooms={setAvailableRooms}
                            addUpdatedRoomsIds={addUpdatedRoomsIds}
                            selectedBuilding={selectedBuilding}
                        />
                    </Box>
                    <Divider style={{marginBottom: "0rem"}}/>
                    <RoleSelectionComponent
                        roleIds={roleIds}
                        setRoleIds={setRoleIds}
                        label={"building-mange-role-selector"}
                        data-testid={"edit-building-roleIds-selector"}
                    ></RoleSelectionComponent>
                    <Divider/>
                </Stack>
            </DialogContent>

            <DialogActions className={isNoFullscreen ? "dialogActionsNoFullScreen" : "dialogActions"}>
                <CloseButton
                    style={{visibility: 'hidden'}}
                    data-testid={"manage-building-close-btn-hidden"}
                />
                <div style={{flex: "1 1 auto"}}></div>
                <Button
                    size={isMobile ? "small" : "medium"}
                    onClick={() => assignAndResetState()}
                    color={"primary"}
                    variant={"contained"}
                    data-testid={"manage-building-save-btn"}
                    disabled={!checkForValidInputNaming(selectedBuilding.buildingName, maxBuildingNameLength) || showNoValidSeatConfigEmail}
                >
                    {t("button_save")}
                </Button>
                <div style={{flex: "1 1 auto"}}></div>
                <CloseButton
                    size={isMobile ? "small" : "medium"}
                    onClick={() => {
                        closeWindow()
                    }}
                    data-testid={"manage-building-close-btn"}
                />
            </DialogActions>
        </Dialog>
    );
};

export default ManageBuildingComponent; // Corrected export statement
